import type { ActionResponse } from "@/lib/actions/types"
import type { Station } from "@/types/domain"
import { StationRole, SystemRole } from "@/types/common"
import { executeRpc } from "@/services/baseApi"
import { logErrorToConsole } from "@/utils/errorHandlerUtils"
import { supabase } from "@/integrations/supabase/client"

// Valida un rol de estación proveniente de la BD; cae a VIEWER si no es válido.
function validateStationRole(value: string): StationRole {
  if (Object.values(StationRole).includes(value as StationRole)) {
    return value as StationRole
  }
  logErrorToConsole(new Error(`Rol de estación inválido recibido: "${value}"`), {
    module: 'MembershipsService',
    operation: 'validateStationRole'
  })
  return StationRole.VIEWER
}

export interface MembershipsAndStationsData {
  memberships: Membership[]
  stations: Station[]
}

// Acceso (estación + rol) con el que se invitó a un usuario aún pendiente.
interface PendingInvitationAccess {
  station_id: string
  station_name: string
  station_role: StationRole
}

// Invitación pendiente: usuario invitado que aún no ha fijado contraseña.
// - 'pending':    aún no verificó el enlace del correo.
// - 'incomplete': el token del enlace ya se consumió (p. ej. lo abrió un
//   escáner de correo) pero nunca fijó contraseña → no puede iniciar sesión.
type PendingInvitationStatus = 'pending' | 'incomplete'

export interface PendingInvitation {
  id: string
  email: string
  invited_at: string
  status: PendingInvitationStatus
  accesses: PendingInvitationAccess[]
}

// Membresía enriquecida (incluye datos de perfil y estación) tal como la entrega
// el RPC get_user_memberships_and_stations.
interface Membership {
  id: string
  profile_id: string
  station_id: string
  station_role: StationRole
  is_active: boolean
  profile_email: string
  profile_role: SystemRole
  station_name: string
}

export async function fetchMembershipsAndStations(): Promise<ActionResponse<MembershipsAndStationsData>> {
  try {
    // La sesión local basta para el uid: el RPC valida el token en el servidor.
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user

    if (!user?.id) {
      return {
        status: 'success',
        data: { memberships: [], stations: [] }
      }
    }

    const response = await executeRpc<Array<{ memberships: Membership[]; stations: Station[] }>>(
      'get_user_memberships_and_stations',
      { user_id: user.id }
    )

    if (response.status === 'error') {
      return {
        status: 'error',
        error: {
          message: response.error?.message ?? 'Error al obtener los accesos',
          code: response.error?.code
        }
      }
    }

    if (!response.data) {
      return {
        status: 'success',
        data: { memberships: [], stations: [] }
      }
    }

    // El RPC `RETURNS TABLE (memberships json, stations json)`: supabase-js entrega un
    // ARRAY de filas. La función devuelve una sola fila con las dos columnas, así que
    // tomamos la primera.
    const row = response.data[0]
    return {
      status: 'success',
      data: {
        memberships: row?.memberships ?? [],
        stations: row?.stations ?? []
      }
    }
  } catch (error) {
    logErrorToConsole(error, { module: 'MembershipsService', operation: 'fetchMembershipsAndStations' })
    return {
      status: 'error',
      error: {
        message: 'Ocurrió un error inesperado al obtener los accesos y estaciones. Intenta nuevamente o contacta soporte.',
        code: 'UNEXPECTED_ERROR'
      }
    }
  }
}

// Normaliza una fila de get_pending_invitations: cualquier estado desconocido
// cuenta como 'pending' y los roles inválidos caen a VIEWER.
export function normalizePendingInvitation(row: PendingInvitation): PendingInvitation {
  return {
    id: row.id,
    email: row.email,
    invited_at: row.invited_at,
    status: row.status === 'incomplete' ? 'incomplete' : 'pending',
    accesses: (row.accesses ?? []).map((a) => ({
      station_id: a.station_id,
      station_name: a.station_name,
      station_role: validateStationRole(a.station_role)
    }))
  }
}

// Lista las invitaciones pendientes que el usuario puede gestionar (el RPC las
// acota a sus estaciones). `RETURNS TABLE`: supabase-js entrega el ARRAY de filas.
export async function fetchPendingInvitations(): Promise<ActionResponse<PendingInvitation[]>> {
  try {
    const response = await executeRpc<PendingInvitation[]>('get_pending_invitations')

    if (response.status === 'error') {
      return {
        status: 'error',
        error: response.error || {
          message: 'No se pudieron obtener las invitaciones pendientes',
          code: 'FETCH_FAILED'
        }
      }
    }

    return {
      status: 'success',
      data: (response.data ?? []).map(normalizePendingInvitation)
    }
  } catch (err) {
    logErrorToConsole(err, { module: 'MembershipsService', operation: 'fetchPendingInvitations' })
    return {
      status: 'error',
      error: {
        message: err instanceof Error ? err.message : 'Error desconocido',
      }
    }
  }
}
